// Champ de lieu d'une course (transport, lot T1b-1) : prise en charge ou dépose.
// - En tapant 2 lettres ou plus, le carnet propose les lieux dont le nom ou l'adresse correspond (8 au plus) ;
//   choisir un lieu met son NOM COURT dans le champ et garde le lien (placeId) ; l'adresse complète s'affiche dessous.
// - On peut toujours taper une adresse libre : modifier le texte retire le lien. Un texte identique au nom d'un lieu
//   actif est relié automatiquement en quittant le champ.
// - Sous une adresse libre, « + Enregistrer dans le carnet » ouvre un petit formulaire pré-rempli.
// - Lot T1b-3 : la liste s'affiche PAR-DESSUS la fenêtre, collée sous le champ, sans rien décaler (avant, elle prenait sa
//   place : en cliquant sur « Enregistrer », elle se refermait, le bouton bougeait et le clic était perdu). 3 suggestions
//   visibles sur téléphone, 5 sur ordinateur, les autres en faisant défiler la liste. L'adresse d'un nom tapé en entier
//   s'affiche dès la frappe (rien n'apparaît plus en quittant le champ). Clavier : ↑ ↓ Entrée ; Échap ferme la liste
//   seulement (la fenêtre de course reste ouverte). La suggestion surlignée est annoncée aux lecteurs d'écran.
//   Sur téléphone, toucher le champ le fait monter en haut de la fenêtre : la liste tient au-dessus du clavier.
import React, { useState, useEffect, useLayoutEffect, useRef, useId } from "react";
import api, { apiError } from "@/lib/api";
import { PLACE_KINDS, searchPlaces, sameName } from "@/lib/places";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Plus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const SAVE_EMPTY = { open: false, name: "", address: "", kind: "other", saving: false };
// Téléphone ou tablette : le clavier va couvrir le bas de l'écran.
const SMALL_OR_TOUCH = "(max-width: 639px), (pointer: coarse)";
const PHONE = "(max-width: 639px)"; // même limite que « sm » de Tailwind

// Fait monter le champ en haut de la zone qui défile (la fenêtre de course), en gardant son libellé visible.
function bringToTop(input) {
  let box = input.parentElement;
  while (box && !(/(auto|scroll)/.test(getComputedStyle(box).overflowY) && box.scrollHeight > box.clientHeight)) box = box.parentElement;
  if (!box) return;
  box.scrollTop += input.getBoundingClientRect().top - box.getBoundingClientRect().top - 32;
}

// `disabled` (lot T5-0) : champ grisé et lien « Enregistrer dans le carnet » caché (course attribuée, secrétaire).
export default function PlaceInput({ value, placeId, places, onChange, onPlaceCreated, placeholder, testid, disabled = false }) {
  const [open, setOpen] = useState(false); // liste des suggestions affichée
  const [active, setActive] = useState(0); // suggestion surlignée (clavier)
  const [save, setSave] = useState(SAVE_EMPTY);
  const listRef = useRef(null);
  const listId = useId();
  const linked = placeId ? (places || []).find((p) => p.id === placeId) : null;
  const matches = open && !placeId ? searchPlaces(places, value) : [];
  const exact = (places || []).find((p) => p.active !== false && sameName(p.name, value));
  const shown = linked || (!placeId ? exact : null); // lieu dont l'adresse s'affiche sous le champ
  const optionId = (i) => `${listId}-option-${i}`;
  const matchKey = matches.map((p) => p.id).join();

  // Hauteur de la liste : 3 suggestions entières sur téléphone, 5 sur ordinateur, plus la moitié de la suivante (on voit
  // qu'elle défile). Mesurée sur les suggestions affichées : une adresse longue passe sur deux lignes.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const next = list.children[window.matchMedia?.(PHONE).matches ? 3 : 5];
    list.style.maxHeight = next ? `${next.offsetTop + next.offsetHeight / 2}px` : "";
  }, [matchKey]);

  // La liste défile pour garder la suggestion surlignée visible (flèches du clavier).
  useEffect(() => {
    const list = listRef.current;
    const el = list?.children[active];
    if (!el) return;
    if (el.offsetTop < list.scrollTop) list.scrollTop = el.offsetTop;
    else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight;
  }, [active, matches.length]);

  const choose = (p) => { onChange({ text: p.name, placeId: p.id }); setOpen(false); setSave(SAVE_EMPTY); };
  const onType = (text) => { onChange({ text, placeId: "" }); setOpen(true); setActive(0); };
  const onKeyDown = (e) => {
    if (!matches.length) return;
    if (e.key === "Escape") setOpen(false); // la fenêtre de course ne se ferme pas (voir RideDialog)
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(matches[active] || matches[0]); }
  };
  const onFocus = (e) => {
    setOpen(true);
    if (window.matchMedia?.(SMALL_OR_TOUCH).matches) bringToTop(e.target);
  };
  const onBlur = () => {
    setOpen(false);
    if (!placeId && exact) onChange({ text: exact.name, placeId: exact.id }); // nom tapé en entier : relié
  };

  const saveMissing = !save.name.trim() || !save.address.trim();
  const createPlace = async () => {
    if (saveMissing) return;
    setSave((s) => ({ ...s, saving: true }));
    try {
      const { data } = await api.post("/places", { name: save.name, address: save.address, kind: save.kind });
      onPlaceCreated?.(data);
      onChange({ text: data.name, placeId: data.id });
      toast.success("Lieu enregistré dans le carnet");
      setSave(SAVE_EMPTY);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setSave((s) => ({ ...s, saving: false })); }
  };

  return (
    <div className="space-y-1.5 min-w-0">
      <div className="relative">
        <Input value={value} onChange={(e) => onType(e.target.value)} onFocus={onFocus} onBlur={onBlur} onKeyDown={onKeyDown}
          placeholder={placeholder} autoComplete="off" role="combobox" aria-autocomplete="list" aria-expanded={matches.length > 0}
          aria-controls={matches.length > 0 ? listId : undefined} aria-activedescendant={matches.length > 0 ? optionId(active) : undefined}
          disabled={disabled} data-testid={testid} />
        {/* Par-dessus la fenêtre, collée sous le champ ; hauteur réglée plus haut (3 ou 5 suggestions visibles). */}
        {matches.length > 0 && (
          <div ref={listRef} id={listId} role="listbox" data-testid={`${testid}-suggestions`}
            className="absolute left-0 right-0 top-full mt-1 z-20 overflow-y-auto overscroll-contain rounded-xl border border-border bg-popover shadow-md">
            {matches.map((p, i) => (
              <button key={p.id} id={optionId(i)} type="button" role="option" aria-selected={i === active} tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()} onClick={() => choose(p)} onMouseEnter={() => setActive(i)}
                className={cn("w-full text-left px-3 py-2 text-sm flex flex-col min-w-0", i === active && "bg-muted")} data-testid={`${testid}-option-${p.id}`}>
                <span className="font-medium break-words">{p.name}</span>
                <span className="text-xs text-muted-foreground break-words">{PLACE_KINDS[p.kind] || ""}{p.address ? ` · ${p.address}` : ""}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {shown && (
        <p className="text-xs text-muted-foreground flex items-start gap-1 break-words" data-testid={`${testid}-address`}>
          <MapPin className="h-3 w-3 mt-0.5 shrink-0 text-accent" /><span className="min-w-0">{shown.address}</span>
        </p>
      )}
      {/* Zone de clic de 28 px de haut, même apparence : marge intérieure compensée par une marge négative (comme au lot 22). */}
      {!disabled && !placeId && !exact && value.trim() && !save.open && (
        <button type="button" onMouseDown={(e) => e.preventDefault()}
          onClick={() => { setOpen(false); setSave({ ...SAVE_EMPTY, open: true, name: value.trim(), address: value.trim() }); }}
          className="text-xs text-accent flex items-center gap-1 py-1.5 -my-1.5" data-testid={`${testid}-save`}>
          <Plus className="h-3.5 w-3.5" /> Enregistrer dans le carnet
        </button>
      )}
      {save.open && (
        <div className="p-3 rounded-xl border border-border space-y-3" data-testid={`${testid}-save-form`}>
          <div className="space-y-1.5"><Label>Nom court</Label>
            <Input value={save.name} onChange={(e) => setSave((s) => ({ ...s, name: e.target.value }))} placeholder="Ex. Hôtel de Paris" data-testid={`${testid}-save-name`} /></div>
          <div className="space-y-1.5"><Label>Adresse complète</Label>
            <Input value={save.address} onChange={(e) => setSave((s) => ({ ...s, address: e.target.value }))} data-testid={`${testid}-save-address`} /></div>
          <div className="space-y-1.5"><Label>Type</Label>
            <Select value={save.kind} onValueChange={(v) => setSave((s) => ({ ...s, kind: v }))}>
              <SelectTrigger data-testid={`${testid}-save-kind`}><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PLACE_KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
          {saveMissing && <p className="text-xs text-destructive" data-testid={`${testid}-save-error`}>Le nom court et l'adresse sont obligatoires.</p>}
          <div className="flex justify-end gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSave(SAVE_EMPTY)} data-testid={`${testid}-save-cancel`}>Annuler</Button>
            <Button size="sm" className="rounded-full" onClick={createPlace} disabled={saveMissing || save.saving} data-testid={`${testid}-save-ok`}>Enregistrer</Button>
          </div>
        </div>
      )}
    </div>
  );
}
